import { EmployeeDirectoryListing } from '@/types/employees';
import { Notification } from '@/types/notifications';
import { NotificationIcon } from '../NotificationIcon';
import { NotificationItemHeader } from '../NotificationItemHeader';
import { NotificationItemProps } from '../types';
import { NotificationType } from '@electricjs/core_entity-client';
import { useGetEmployeeFromDirectoryQuery } from '@/redux/slices/employeeApiSlice';
import { useGetOrganizationId } from '@/hooks/useGetOrganizationId';
import { Button } from '@electricjs/arc';
import { useNavigate } from 'react-router-dom';
import { Trans } from 'react-i18next';

import i18n from '@/i18n';
import { getDisplayFullName } from 'common/utils/getFullName';

// Note: This is taking a notification because the ActionColumn for the notification center only accepts that as a parameter
export const ReviewXboardingTasksAction = ({
  notification,
}: {
  notification: Notification;
}) => {
  const navigate = useNavigate();
  return (
    <Button
      id="button-review-tasks"
      alignSelf="flex-start"
      gap="0.8rem"
      my="0.8rem"
      onClick={() => {
        navigate(
          `/tasks?requestCollectionId=${notification.data.request_collection_id}&onBehalfOf=${notification.data.employee_id}&tasks=my`
        );
      }}>
      {i18n.t('common:notificationItems.xboardingTasks.reviewTasks')}
    </Button>
  );
};

export const getTitle = (
  notification: Notification,
  employee: EmployeeDirectoryListing
) => {
  const employeeName = employee
    ? getDisplayFullName(employee)
    : i18n.t('common:notificationItems.anEmployee');

  const startDate = notification.data.startDate ?? '';
  switch (notification.type) {
    case NotificationType.OnboardingTasks:
      return (
        <>
          <Trans
            i18nKey="notificationItems.xboardingTasks.onboardingTitle"
            ns="common"
            values={{ employeeName }}
            components={{ bold: <b /> }}
          />
          <br />
          {startDate ? (
            <Trans
              i18nKey="notificationItems.xboardingTasks.onboardingSubtitleWithDate"
              ns="common"
              values={{ startDate }}
              components={{ bold: <b /> }}
            />
          ) : (
            i18n.t(
              'common:notificationItems.xboardingTasks.onboardingSubtitleNoDate'
            )
          )}
        </>
      );
    case NotificationType.OffboardingTasks:
      return (
        <>
          <Trans
            i18nKey="notificationItems.xboardingTasks.offboardingTitle"
            ns="common"
            values={{ employeeName }}
            components={{ bold: <b /> }}
          />
          <br />
          {i18n.t(
            'common:notificationItems.xboardingTasks.offboardingSubtitle'
          )}
        </>
      );
    default:
      return <></>;
  }
};

const XboardingTasksNotificationItem = ({
  notification,
  hideDetails,
}: NotificationItemProps) => {
  const organizationId = useGetOrganizationId();
  const { data: employee } = useGetEmployeeFromDirectoryQuery({
    employeeId: notification.data.employee_id,
    organizationId,
  });

  if (!employee) {
    return null;
  }

  const isOnboarding = notification.type === NotificationType.OnboardingTasks;

  return (
    <NotificationItemHeader
      notification={notification}
      title={getTitle(notification, employee)}
      icon={
        <NotificationIcon
          isNotificationUnseen={!notification.seenAt}
          icon={isOnboarding ? 'sun-rise' : 'moon-outline'}
          intent={isOnboarding ? 'warning' : 'primary'}
        />
      }
      label={
        isOnboarding
          ? i18n.t('common:notificationItems.labels.onboarding')
          : i18n.t('common:notificationItems.labels.offboarding')
      }>
      {!hideDetails ? (
        <ReviewXboardingTasksAction notification={notification} />
      ) : null}
    </NotificationItemHeader>
  );
};

export default XboardingTasksNotificationItem;
